<script setup lang="ts">
import ProduktionseinheitenImportMatchPanel, { type SelectionChangeEvent } from '@/components/PronovoMatchPanel.vue';
import Button from 'primevue/button';
import Toolbar from 'primevue/toolbar';
import Card from 'primevue/card';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import KpJahrSelect from '@/components/KpJahrSelect.vue';
import KpMandantKantonSelect from '@/components/KpMandantKantonSelect.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import { useI18n } from 'vue-i18n';
import { globalKantonCollection } from '@/models/Kanton';
import JobList from '@/components/JobList.vue';
import { JOB_TYP } from '@/models/Job';
import KpFileUpload from '@/components/KpFileUpload.vue';
import { instance as api } from '@/helpers/api';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { ZuordnungImportHeaderCollection } from '@/models/ZuordnungImportHeader';

const toast = useToast();
const fileSelected = ref(false);
const loading = ref(false);
const jahr: Ref<number | null> = ref(null);
const kantonId: Ref<number | null> = ref(null);
const step = ref('1');
const fileUpload: Ref<typeof KpFileUpload | null> = ref(null);
const additionalUploadParams: Ref<Record<string, any>> = ref({});

async function upload() {
    loading.value = true;
    const res = await api().post('/Gebaeude/hasEntries', {
        jahr: jahr.value,
        kanton_id: kantonId.value,
    });
    additionalUploadParams.value.jahr = jahr.value;
    additionalUploadParams.value.kanton_id = kantonId.value;
    additionalUploadParams.value.import_type = 'produktionseinheiten';
    if (res.data.hasEntries) {
        showOverwriteWarning();
    } else {
        await executeFileUpload();
    }
}

const availableHeaders = ref([]);
let jobId: number | null = null;

const kantone = globalKantonCollection();
const t = useI18n().t;
const confirm = useConfirm();
function showOverwriteWarning() {
    const kanton = kantone.find((model) => model.id === kantonId.value);
    confirm.require({
        message: t('daten_fuer_jahr_existieren_schon_warnung', { jahr: jahr.value, kanton: kanton?.bezeichnung }),
        header: t('achtung'),
        rejectProps: {
            label: t('nein'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('ja'),
        },
        accept: () => executeFileUpload(),
        reject: () => (loading.value = false),
    });
}

async function executeFileUpload() {
    try {
        await fileUpload.value?.uploadFiles();
    } catch (e) {
        errToToast(e, toast);
    }
}

function onFileUploaded(message: string) {
    step.value = '2';
    loading.value = false;
    const res = JSON.parse(message);
    availableHeaders.value = res?.headers;
    jobId = res?.jobId;
}

function onFileSelected(files: File[]) {
    fileSelected.value = files.length > 0;
}

async function startJob() {
    await api().post('/HeaderVariable/updateMapping', {
        matchmap: Object.fromEntries(matchMap.entries()),
    });
    zihCollection.query();
    if (jobId) {
        await api().post('/Job/activateJob', {
            id: jobId,
        });
    }
    step.value = '3';
}

function resetStepper(resetYearKanton: boolean) {
    if (resetYearKanton) {
        jahr.value = null;
        kantonId.value = null;
    }
    step.value = '1';
}

const validationMap: Ref<Map<number, boolean>> = ref(new Map());
const matchMap = new Map<number, number | null>();
function onMatchPanelSelectionChange(event: SelectionChangeEvent) {
    if (event.zuordnungImportHeaderId) {
        validationMap.value.set(event.zuordnungImportHeaderId, !!event.headerVariableId);
        if (event.headerVariableId) {
            matchMap.set(event.zuordnungImportHeaderId, event.headerVariableId);
        }
    }
}

const disableNextButton = computed(() => {
    let allClear = true;
    validationMap.value.forEach((v) => (allClear = allClear && v));
    return !allClear;
});

const zihCollection: ZuordnungImportHeaderCollection = new ZuordnungImportHeaderCollection();

onMounted(async () => {
    zihCollection.setQueryParams({
        filter: [
            {
                id: 'target_tabelle',
                property: 'target_tabelle',
                value: 'produktionseinheiten',
            },
        ],
    });
    await zihCollection.query();
});
</script>

<template>
    <Card class="kp-max-content-width">
        <template #header>
            <Toolbar class="toolbar border-none">
                <template #start>
                    <h2><i class="fa-regular fa-file-import"></i> {{ $t('produktionseinheiten_import') }}</h2>
                </template>
            </Toolbar>
        </template>
        <template #content>
            <Stepper v-model:value="step" linear>
                <StepList>
                    <Step value="1">{{ $t('fileupload') }}</Step>
                    <Step value="2">{{ $t('feldzuweisung') }}</Step>
                    <Step value="3">{{ $t('abschluss') }}</Step>
                </StepList>
                <StepPanels>
                    <StepPanel v-slot="{}" value="1">
                        <div class="flex flex-column justify-content-center flex-wrap">
                            <div class="m-auto w-12rem">
                                <KpJahrSelect
                                    v-model="jahr"
                                    fluid
                                    :label="$t('jahr')"
                                    :start-year="2023"
                                    :disabled="loading"
                                ></KpJahrSelect>
                            </div>
                            <div class="pb-2rem m-auto w-12rem">
                                <KpMandantKantonSelect
                                    v-model="kantonId"
                                    fluid
                                    :label="$t('kanton')"
                                    :disabled="loading"
                                ></KpMandantKantonSelect>
                            </div>
                            <div>
                                <KpFileUpload
                                    v-if="step === '1'"
                                    ref="fileUpload"
                                    :additional-params="additionalUploadParams"
                                    accept=".xlsx"
                                    @file-selected="onFileSelected"
                                    @file-uploaded="onFileUploaded"
                                ></KpFileUpload>
                            </div>
                        </div>
                        <div class="flex pt-6 justify-content-end">
                            <Button
                                :label="$t('upload')"
                                :disabled="!fileSelected || !kantonId || !jahr || loading"
                                :loading="loading"
                                icon="fa-solid fa-arrow-right"
                                iconPos="right"
                                @click="() => upload()"
                            ></Button>
                        </div>
                    </StepPanel>
                    <StepPanel v-slot="{}" value="2">
                        <ProduktionseinheitenImportMatchPanel
                            :csvHeaders="availableHeaders"
                            :zuordnung-models="zihCollection.getModels()"
                            @selection-change="onMatchPanelSelectionChange"
                        ></ProduktionseinheitenImportMatchPanel>
                        <div class="flex pt-6 justify-content-between">
                            <Button
                                :label="$t('zurueck')"
                                severity="secondary"
                                icon="fa-solid fa-arrow-left"
                                @click="resetStepper(false)"
                            ></Button>
                            <Button
                                :label="$t('weiter')"
                                :disabled="disableNextButton"
                                icon="fa-solid fa-arrow-right"
                                iconPos="right"
                                @click="() => startJob()"
                            ></Button>
                        </div>
                    </StepPanel>
                    <StepPanel value="3">
                        <div class="flex flex-col h-48">
                            <div>{{ $t('import_job_erstellt') }}</div>
                        </div>
                        <div class="flex pt-6 justify-content-between">
                            <Button
                                :label="$t('weiteren_import')"
                                severity="secondary"
                                icon="fa-solid fa-arrow-left"
                                @click="resetStepper(true)"
                            ></Button>
                        </div>
                    </StepPanel>
                </StepPanels>
            </Stepper>
            <Card v-if="step !== '2'">
                <template #header>
                    <h2 class="ml-1rem">{{ $t('laufende_jobs') }}</h2>
                </template>
                <template #content>
                    <JobList :typ="JOB_TYP.PRODUKTIONSEINHEITEN_IMPORT"></JobList>
                </template>
            </Card>
        </template>
    </Card>
    <ConfirmDialog
        ><template #message="slotProps">
            <div v-html="slotProps.message.message"></div>
        </template>
    </ConfirmDialog>
</template>
